/**
 * Perceptually uniform ramps (SPEC §5).
 *
 * These are polynomial fits to viridis/inferno/magma, which are uniform in
 * lightness — the point being that equal steps in turbulence intensity look
 * like equal steps, so the legend means something. A rainbow ramp would create
 * false boundaries at the yellow and cyan bands, which on a safety-adjacent
 * tool is not a cosmetic problem.
 */

export type RGB = [number, number, number];

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

function poly(t: number, c: number[]): number {
  let v = 0;
  for (let i = c.length - 1; i >= 0; i--) v = v * t + c[i];
  return clamp01(v);
}

/**
 * Turbulence intensity at the top of the ramp.
 *
 * TI is σ_u/U — the fluctuation as a fraction of the mean wind — so the scale
 * can be anchored to what the air is physically doing rather than to an
 * arbitrary maximum:
 *
 *   ~0.10  open flat terrain in neutral air; the practical floor outdoors
 *   ~0.15  normal flying air, lightly textured
 *   ~0.20  rough or forested ground; noticeably active
 *   ~0.30  strong shear or the edge of a wake — early rotor signs
 *   ~0.40  separated flow; fluctuations approaching half the mean wind
 *   ~0.50  fully detached, recirculating air. At any soaring wind speed the
 *          gusts here are a large fraction of trim speed, which is collapse
 *          territory for any wing.
 *
 * 0.5 rather than the previous 0.6: values above it are not meaningfully worse
 * to a pilot — both mean "do not be here" — and stretching the ramp to 0.6 spent
 * a fifth of the scale on that distinction instead of on the 0.15–0.35 range
 * where the useful judgement actually happens.
 */
export const TI_DISPLAY_MAX = 0.5;

/**
 * Gust strength σ_u (m/s) at the top of the ramp, and the scale's soft knee.
 *
 * Calibrated against what the model actually produces, not against a
 * theoretical worst case. Measured over Tiger Mountain in a 35 km/h westerly —
 * a strong day for that site — σ_u spanned 1.25 m/s mean to 2.58 m/s peak
 * across the whole domain. On the previous 9 m/s top that occupies ramp
 * positions 0.37 to 0.58: a third of the scale, entirely within the yellows,
 * which is why the layer read as flat and uninformative everywhere.
 *
 * At 3.5 m/s the same day spans 0.56 to 0.86 — yellow through orange into red.
 * Identical physics, legible map. 3.5 m/s is also a defensible ceiling on its
 * own terms: fluctuations of that size are a large fraction of a paraglider's
 * trim speed, so there is little to gain from resolving beyond it.
 *
 * The scale stays logarithmic because the low end still matters: telling 0.5
 * from 1.0 m/s is as useful as telling 2 from 3.
 */
export const GUST_DISPLAY_MAX = 3.5;
const GUST_KNEE = 0.8;

/** Map a gust value in m/s to ramp position 0–1. */
export function gustRampPosition(gustMs: number): number {
  const x = Math.max(0, gustMs);
  return clamp01(Math.log1p(x / GUST_KNEE) / Math.log1p(GUST_DISPLAY_MAX / GUST_KNEE));
}

/** Gust value in m/s at a given ramp position — for labelling the legend. */
export function gustAtRampPosition(t: number): number {
  return GUST_KNEE * (Math.pow(1 + GUST_DISPLAY_MAX / GUST_KNEE, clamp01(t)) - 1);
}

/**
 * Speed-up is a *ratio* — local wind over free stream — so the scale is
 * logarithmic and symmetric about 1.0. Half speed and double speed sit the same
 * distance either side of centre, which a linear scale cannot do: it would put
 * 0.5x much closer to 1.0 than 2.0x is.
 *
 * The range is one octave each way. Sheltered lee pockets reach roughly 0.2x
 * and gap compression roughly 2x, so anything beyond clamps rather than
 * stretching the useful middle.
 */
export const SPEEDUP_OCTAVES = 1;

/** Map a speed-up ratio to ramp position, 0.5 = unchanged. */
export function speedUpRampPosition(ratio: number): number {
  if (!(ratio > 0)) return 0.5;
  return clamp01(0.5 + Math.log2(ratio) / (2 * SPEEDUP_OCTAVES));
}

/** Speed-up ratio at a ramp position — for labelling the legend. */
export function speedUpAtRampPosition(t: number): number {
  return Math.pow(2, (clamp01(t) - 0.5) * 2 * SPEEDUP_OCTAVES);
}

type Stop = [t: number, r: number, g: number, b: number];

function piecewise(stops: Stop[], t: number): RGB {
  const x = clamp01(t);
  for (let i = 0; i < stops.length - 1; i++) {
    const [t0, r0, g0, b0] = stops[i];
    const [t1, r1, g1, b1] = stops[i + 1];
    if (x <= t1) {
      const f = t1 === t0 ? 0 : (x - t0) / (t1 - t0);
      return [r0 + (r1 - r0) * f, g0 + (g1 - g0) * f, b0 + (b1 - b0) * f];
    }
  }
  const last = stops[stops.length - 1];
  return [last[1], last[2], last[3]];
}

/**
 * Turbulence ramp: deep blue → azure → cyan → green → yellow → amber → orange
 * → red → magenta.
 *
 * Nine stops rather than six, because the judgement that matters sits in the
 * middle of the range — telling "normal flying air" from "the edge of a wake"
 * — and a six-stop ramp ran green straight into yellow across exactly that
 * band. The extra stops buy resolution where it is used.
 *
 * The top is magenta, off the warm sequence entirely. Red reads as "a lot of
 * the same thing"; violet reads as a different kind of thing, which is the
 * right signal for air that has stopped being turbulent-but-flyable and become
 * recirculating. It also stays distinguishable from the orange rotor hatching
 * drawn over it.
 *
 * This trades some of viridis's perceptual uniformity for range and for a top
 * end that carries meaning. Lightness rises monotonically to the yellow and
 * then falls, so ordering stays readable, and the hue sequence is the one
 * pilots already read on every other weather product.
 */
export function turbulenceRamp(t: number): RGB {
  return piecewise(
    [
      [0.0, 0.09, 0.24, 0.52],
      [0.16, 0.06, 0.52, 0.76],
      [0.28, 0.05, 0.70, 0.70],
      [0.40, 0.24, 0.78, 0.36],
      [0.52, 0.80, 0.88, 0.16],
      [0.64, 0.99, 0.68, 0.08],
      [0.76, 0.98, 0.40, 0.06],
      [0.88, 0.87, 0.10, 0.16],
      [1.0, 0.78, 0.16, 0.78],
    ],
    t
  );
}

/**
 * Diverging ramp for speed-up: sheltered blue, unchanged neutral, compressed
 * red.
 *
 * Deliberately a different family from the turbulence ramp. Speed-up is a
 * two-sided quantity where the middle is the uninteresting case, so the centre
 * is a desaturated grey that the alpha ramp then makes nearly invisible — the
 * map should only mark air that is doing something other than carrying on at
 * free-stream speed. A sequential ramp would put the most visually loud colour
 * at one extreme and hide the other.
 */
export function speedUpRamp(t: number): RGB {
  return piecewise(
    [
      [0.0, 0.09, 0.25, 0.60],
      [0.22, 0.20, 0.52, 0.82],
      [0.40, 0.62, 0.76, 0.88],
      [0.5, 0.86, 0.86, 0.84],
      [0.60, 0.95, 0.80, 0.52],
      [0.78, 0.96, 0.52, 0.18],
      [1.0, 0.80, 0.09, 0.12],
    ],
    t
  );
}

/**
 * Glide ratios the hike-and-fly ramp spans, as 1:R.
 *
 * The good end stops at 1:1 rather than at the best value the terrain
 * produces. Anything steeper than a 45° fall line is a clean launch by any
 * measure, and letting the scale chase an outlier cliff would compress every
 * distinction a pilot actually makes into the bottom third of the ramp. The
 * far end is the point past which terrain has stopped being the limiting
 * factor and the wing has become it.
 */
export const GLIDE_BEST_RATIO = 1;
export const GLIDE_WORST_RATIO = 8;

/** Ramp position for a required glide ratio: 1 is the cleanest drop. */
export function glideRampPosition(ratio: number): number {
  const r = Math.max(ratio, 1e-3);
  return clamp01(
    1 - Math.log(r / GLIDE_BEST_RATIO) / Math.log(GLIDE_WORST_RATIO / GLIDE_BEST_RATIO)
  );
}

/** Inverse of `glideRampPosition`, for labelling the legend. */
export function glideAtRampPosition(t: number): number {
  return (
    GLIDE_BEST_RATIO * Math.pow(GLIDE_WORST_RATIO / GLIDE_BEST_RATIO, 1 - clamp01(t))
  );
}

/**
 * Trim speed assumed for the headwind penalty, m/s (≈35 km/h).
 *
 * A single number standing in for every wing, which is the honest compromise:
 * the layer already refuses to model glider performance, and trim varies less
 * between wings than the terrain varies between slopes. Recreational wings trim
 * around 35–38 km/h; the lower end is the conservative choice here, because a
 * lower trim speed makes the headwind penalty bite harder.
 */
export const TRIM_SPEED_MS = 35 / 3.6;

/**
 * How much of a wing's still-air glide survives a headwind.
 *
 * Ground glide is `G · (1 − W/V_trim)`: sink rate is unchanged, but the ground
 * covered per second falls by the headwind component. A 1:8 wing trimming at
 * 35 km/h into a 17.5 km/h headwind is gliding 1:4 over the ground — half its
 * still-air performance for a wind that is nothing special.
 *
 * Floored rather than allowed to reach zero: at `W → V_trim` the wing is
 * parked or going backwards and the arithmetic stops meaning anything. The
 * floor pushes the requirement off the top of the scale instead, which fades
 * the ground out — the right outcome, arrived at without a singularity.
 *
 * A tailwind is deliberately given no credit. It genuinely extends ground
 * glide, but a downwind launch is not a launch, and there is no version of this
 * layer that should paint one as a better option than it was in still air.
 */
export function headwindGlideFactor(headwindMs: number, trimMs = TRIM_SPEED_MS): number {
  return Math.max(1 - Math.max(headwindMs, 0) / trimMs, 0.15);
}

/**
 * Hike-and-fly suitability: pale sand at the marginal end, deep green at the
 * clean-drop end.
 *
 * A fourth colour family, chosen so it cannot be mistaken for the three that
 * describe air. Green carries an unavoidable "good" reading, which is right
 * here — this is a suitability scale, not a hazard scale — but it is also why
 * the layer must not be read as a verdict on the *air* over that ground. The
 * legend says so, and the air-character layer is one click away.
 */
export function hikeFlyRamp(t: number): RGB {
  return piecewise(
    [
      [0.0, 0.80, 0.76, 0.64],
      [0.25, 0.85, 0.72, 0.36],
      [0.5, 0.70, 0.74, 0.24],
      [0.75, 0.36, 0.66, 0.30],
      [1.0, 0.09, 0.45, 0.30],
    ],
    t
  );
}

/**
 * Watermark: the air below the chosen altitude, shaded by how hard the terrain
 * is squeezing the flow through it.
 *
 * A water palette on purpose — the layer reads as a flood filling the valleys,
 * which is the mental picture it is there to give. Dark still-water blue for
 * sheltered air, rising through steel and cyan to near-white where a gap or a
 * gorge is accelerating the flow. Sequential, because unlike speed-up the
 * sheltered end is not a symmetric opposite here: it is simply the calm end.
 */
export function watermarkRamp(t: number): RGB {
  return piecewise(
    [
      [0.0, 0.05, 0.13, 0.32],
      [0.3, 0.09, 0.30, 0.55],
      [0.55, 0.13, 0.52, 0.72],
      [0.78, 0.34, 0.78, 0.85],
      [1.0, 0.88, 0.97, 0.99],
    ],
    t
  );
}

/** Viridis — retained for reference and comparison. */
export function viridis(t: number): RGB {
  const x = clamp01(t);
  return [
    poly(x, [0.2777, 0.1053, -0.3308, -4.6342, 6.2282, 4.7763, -5.4354]),
    poly(x, [0.0054, 1.4046, 0.2148, -5.7991, 14.1799, -13.7451, 4.6453]),
    poly(x, [0.334, 1.3845, 0.399, -5.2993, 4.2216, -0.2939, -0.5262]),
  ];
}

/** Inferno — used for wind speed, where the top end should read as "a lot". */
export function inferno(t: number): RGB {
  const x = clamp01(t);
  return [
    poly(x, [0.0002, 0.1065, 3.3009, -5.1548, 9.6329, -12.2299, 4.5175]),
    poly(x, [0.0016, -0.2274, 2.2828, -8.2371, 15.0122, -12.8949, 4.0435]),
    poly(x, [0.0139, 2.0899, -12.0819, 46.0468, -80.0755, 63.2098, -18.1329]),
  ];
}

/**
 * Streamline ramp. Unlike the raster ramps this never goes dark: particles are
 * thin strokes drawn over hillshade and a turbulence field, so a ramp whose low
 * end is black (as inferno's is) makes slow-moving air invisible. Lightness
 * stays high throughout and only the hue carries the speed.
 */
export function streamlineRamp(t: number): RGB {
  const x = clamp01(t);
  if (x < 0.5) {
    const s = x * 2;
    return [0.72 + 0.28 * s, 0.86 + 0.11 * s, 0.99 - 0.24 * s];
  }
  const s = (x - 0.5) * 2;
  return [1.0, 0.97 - 0.36 * s, 0.75 - 0.5 * s];
}

/** Diverging ramp for vertical velocity: sink blue, neutral transparent, lift warm. */
export function liftRamp(t: number): RGB {
  // t in [0,1] with 0.5 = zero vertical velocity.
  const x = clamp01(t);
  if (x < 0.5) {
    const s = 1 - x * 2;
    return [0.13 + 0.15 * (1 - s), 0.4 + 0.3 * (1 - s), 0.75 + 0.2 * (1 - s)];
  }
  const s = (x - 0.5) * 2;
  return [0.95, 0.85 - 0.5 * s, 0.35 - 0.3 * s];
}

/** Hazard class colours, index-aligned with HAZARD_CLASSES. */
export const HAZARD_COLOURS: RGB[] = [
  [0.35, 0.72, 0.86], // laminar
  [0.45, 0.78, 0.5], // usable
  [0.96, 0.78, 0.29], // rough
  [0.93, 0.5, 0.2], // rotor
  [0.85, 0.24, 0.28], // avoid
];

export const HAZARD_LABELS = ['Laminar', 'Usable', 'Rough', 'Rotor', 'Avoid'] as const;

/**
 * Descriptions for the legend. Deliberately descriptive rather than advisory
 * (SPEC §10): the model cannot know what a given pilot on a given wing can
 * handle, so it reports what the air is doing and stops there.
 */
export const HAZARD_DESCRIPTIONS: Record<string, string> = {
  Laminar: 'Smooth, attached flow',
  Usable: 'Mild texture, flow attached',
  Rough: 'Notable shear or terrain-driven turbulence',
  Rotor: 'Modelled flow separation — recirculating air',
  Avoid: 'Strong separation or extreme modelled turbulence',
};

/** Build a 256×1 RGBA lookup texture from a ramp function. */
export function rampTexture(fn: (t: number) => RGB, alpha?: (t: number) => number): Uint8Array {
  const out = new Uint8Array(256 * 4);
  for (let i = 0; i < 256; i++) {
    const t = i / 255;
    const [r, g, b] = fn(t);
    out[i * 4] = Math.round(r * 255);
    out[i * 4 + 1] = Math.round(g * 255);
    out[i * 4 + 2] = Math.round(b * 255);
    out[i * 4 + 3] = Math.round((alpha ? clamp01(alpha(t)) : 1) * 255);
  }
  return out;
}

export function rgbToCss([r, g, b]: RGB, a = 1): string {
  const c = (v: number) => Math.round(clamp01(v) * 255);
  return a >= 1 ? `rgb(${c(r)},${c(g)},${c(b)})` : `rgba(${c(r)},${c(g)},${c(b)},${a})`;
}
